// Markup cascade + nearest-$5 rounding per APD SOP. Lives in shared/ so the
// server (route handler + audit log) and the client (live preview in the
// customer-quote modal) compute the exact same numbers — no drift.
//
// Build plan §"Markup Model (Option D)":
//   tenant default → customer override → line override → request override
//
// "Request override" is the broker's transient input at quote-gen time. It
// isn't persisted; for persistent line-level overrides, set
// rfq_line_items.markup_pct_override.
//
// Rounding rule (APD-Standard-Operating-Procedure.md §STAGE 3 +
// APD-Soul-ContactMatrix.md): "Always round quoted prices to nearest $5 or $0.
// No decimals on quotes." Example: $55,273.60 → $55,275.00.
//
// $5 floor: aviation brokers don't take orders below ~$5 (wire fee + handling
// overhead would consume the full sale). So sub-$5 raw prices round UP to $5
// rather than collapsing to $0.

export interface MarkupInputs {
  tenantDefaultPct: string | number;
  customerOverridePct?: string | number | null;
  lineOverridePct?: string | number | null;
  requestOverridePct?: number | null;
}

export type MarkupSource = 'tenant' | 'customer' | 'line' | 'request';

export function resolveMarkupPct(inputs: MarkupInputs): number {
  return resolveMarkup(inputs).pct;
}

export function resolveMarkup(inputs: MarkupInputs): { pct: number; source: MarkupSource } {
  const candidates: Array<[unknown, MarkupSource]> = [
    [inputs.requestOverridePct, 'request'],
    [inputs.lineOverridePct, 'line'],
    [inputs.customerOverridePct, 'customer'],
    [inputs.tenantDefaultPct, 'tenant'],
  ];
  for (const [val, source] of candidates) {
    if (val == null) continue;
    const n = typeof val === 'string' ? parseFloat(val) : (val as number);
    if (!Number.isFinite(n)) continue;
    return { pct: n, source };
  }
  return { pct: 10, source: 'tenant' };
}

/**
 * Round a unit price (in major currency units) to the nearest 5, with a $5
 * floor for any positive value. Zero stays zero; negative values clamp to 0.
 */
export function roundToNearest5(value: number): number {
  if (value <= 0) return 0;
  const rounded = Math.round(value / 5) * 5;
  return Math.max(5, rounded);
}

export interface PriceBreakdown {
  vendorUnitCents: number;
  vendorTotalCents: number;
  markupPct: number;
  /** Pre-rounding marked-up unit price in cents. */
  customerUnitCentsRaw: number;
  /** Final customer unit price in cents after rounding to nearest $5. */
  customerUnitCents: number;
  /** customerUnitCents × qty. */
  customerTotalCents: number;
  /** customerTotalCents − vendorTotalCents (gross profit). */
  marginCents: number;
  /** marginCents / customerTotalCents × 100. */
  marginPct: number;
}

export function computePriceBreakdown(
  vendorUnitCents: number,
  qty: number,
  markupPct: number,
): PriceBreakdown {
  const vendorUnit = vendorUnitCents / 100;
  const customerUnitRaw = vendorUnit * (1 + markupPct / 100);
  const customerUnitRounded = roundToNearest5(customerUnitRaw);
  const customerUnitCents = Math.round(customerUnitRounded * 100);
  const customerTotalCents = customerUnitCents * qty;
  const vendorTotalCents = vendorUnitCents * qty;
  const marginCents = customerTotalCents - vendorTotalCents;
  const marginPct = customerTotalCents > 0 ? (marginCents / customerTotalCents) * 100 : 0;
  return {
    vendorUnitCents,
    vendorTotalCents,
    markupPct,
    customerUnitCentsRaw: Math.round(customerUnitRaw * 100),
    customerUnitCents,
    customerTotalCents,
    marginCents,
    marginPct,
  };
}
